import { WarningIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionItem,
  AccordionIcon,
  AccordionPanel,
  AccordionButton,
  Box,
  Flex,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Spacer,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Text,
  Divider,
  IconButton,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Button,
  Checkbox,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import {
  Date as dateType,
  CreditAgreement,
  Util,
} from '@finley-technologies/finley-types';

import { AddConfigButton } from './components/AddConfigButton';
import {
  DaysOfTheWeekSelectButtons,
  handleSelectDays,
} from './components/DayOfTheWeek';
import {
  DayOfTheWeek,
  dayOfTheWeekToNumberMap,
  ConfigFormState,
  PERIOD_OPTIONS,
  OFFSET_DAY_UNIT_OPTIONS,
} from './types';
import {
  downloadRelativeConfig,
  generateHumanReadableString,
  makeAdverb,
  ordinalSuffix,
  convertConfigFormStatesToRelativeRecurringDateConfigs,
  convertRelativeRecurringDateConfigsToConfigFormStates,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { TestTriggerDate } from 'packages/common-frontend/components/RecurringDateForm/components/ReviewDates';
import { Select } from 'packages/common-frontend/components/Select';
import { isAdminDashboard } from 'packages/common-frontend/utils';

export const DateFormulaInput = ({
  errors,
  formula,
  setFormula,
  isDisabled = false,
}: {
  errors: string;
  formula: string;
  setFormula: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}) => {
  const [input, setInput] = React.useState<string>(formula);
  return (
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Icon icon="equals" />
      </InputLeftElement>
      <Input
        type="text"
        placeholder="EOMONTH(triggerDate, -1)"
        _placeholder={{ opacity: 1, color: 'gray.500' }}
        isInvalid={errors.length > 0}
        isDisabled={isDisabled}
        errorBorderColor="red.500"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onBlur={() => setFormula(input)}
      />
    </InputGroup>
  );
};

type RecurringDatesOptionsGridProps = {
  config: ConfigFormState;
  setConfig: React.Dispatch<React.SetStateAction<ConfigFormState[]>>;
  indexOfConfig: number;
  numOfConfigs: number;
  staticStartDateFormula?: string;
  staticEndDateFormula?: string;
};
const RecurringDatesOptionsGrid = ({
  config,
  setConfig,
  indexOfConfig,
  numOfConfigs,
  staticStartDateFormula,
  staticEndDateFormula,
}: RecurringDatesOptionsGridProps) => {
  const greyBgColor = useColorModeValue('gray.200', 'gray.800');

  // --------------------- REACT HOOKS ---------------------
  const [startDateFormula, setStartDateFormula] = React.useState<string>(
    staticStartDateFormula || config.startDateFormula || '',
  );
  const [endDateFormula, setEndDateFormula] = React.useState<string>(
    staticEndDateFormula || config.endDateFormula || '',
  );

  const [frequency, setFrequency] = React.useState<
    | dateType.RecurringDate.FrequencyPeriod
    | dateType.RecurringDate.ReccuringFrequencyPeriod
    | undefined
  >(config.frequency);

  const [cadence, setCadence] = React.useState(config.cadence || 1);
  const [occurrence, setOccurrence] = React.useState(config.occurrence || 1);

  const [
    period,
    setPeriod,
  ] = React.useState<dateType.RecurringDate.BaseDateConfigType>(
    config.period || 'calendarDate',
  );

  const frequencyFilteredOptions = useMemo(() => {
    const frequencyOptions = ['week', 'month', 'quarter', 'year'];

    if (period !== 'weekday') {
      return frequencyOptions.filter((f) => f !== 'week');
    }
    return ['week'];
  }, [period]);

  const [offsetDays, setOffsetDays] = React.useState<number>(
    config.offsetDays || 0,
  );
  const [offsetDayUnit, setoffsetDayUnit] = React.useState<dateType.DayUnit>(
    config.offsetDayUnit || 'calendarDay',
  );

  const [selectedDays, setSelectedDays] = React.useState<DayOfTheWeek[]>(
    config.selectedDays || [],
  );

  const [
    offsetNonBusinessDayAdjustment,
    setOffsetNonBusinessDayAdjustment,
  ] = React.useState<dateType.NonBusinessDayAdjustment | undefined>(
    config.offsetNonBusinessDayAdjustment,
  );

  // --------------------- ERRORS FROM VALIDATING INPUTS ---------------------
  const [errorFrequency, setErrorFrequency] = React.useState<string>('');
  const [errorSelectedDay, setErrorSelectedDays] = React.useState<string>('');
  const [
    errorStartDateFormula,
    setErrorStartDateFormula,
  ] = React.useState<string>('');
  const [errorEndDateFormula, setErrorEndDateFormula] = React.useState<string>(
    '',
  );

  useEffect(() => {
    if (frequency) {
      setErrorFrequency(
        period !== 'weekday' && frequency === 'week'
          ? 'Frequency cannot be `week`'
          : '',
      );
    } else {
      setErrorFrequency('Frequency cannot be empty');
    }
    setErrorSelectedDays(
      (period === 'weekday' || period === 'weekdayByOccurrence') &&
        (!selectedDays || selectedDays.length < 1 || selectedDays.length > 7)
        ? 'Selected days of the week error'
        : '',
    );
    if (startDateFormula) {
      setErrorStartDateFormula(
        recurringDate.validateDateFormula(startDateFormula).errorMessages
          .length > 0
          ? 'Start Date Formula syntax error/typo'
          : '',
      );
    } else {
      setErrorStartDateFormula('Start Date Formula cannot be empty');
    }
    if (endDateFormula) {
      setErrorEndDateFormula(
        recurringDate.validateDateFormula(endDateFormula).errorMessages.length >
          0
          ? 'End Date Formula syntax error/typo'
          : '',
      );
    } else {
      setErrorEndDateFormula('End Date Formula cannot be empty');
    }
  }, [
    endDateFormula,
    startDateFormula,
    frequency,
    occurrence,
    period,
    selectedDays,
  ]);

  const sumOfInputErrors = useMemo(() => {
    const err = [
      errorFrequency,
      errorSelectedDay,
      errorStartDateFormula,
      errorEndDateFormula,
    ]
      .filter((e) => e.length > 0)
      .join(', ');
    return err;
  }, [
    errorFrequency,
    errorSelectedDay,
    errorStartDateFormula,
    errorEndDateFormula,
  ]);

  // --------------------- VALIDATE & CREATE CONFIG ---------------------
  useEffect(() => {
    // relative recurring date config (accepts date formula)
    const result = recurringDate.validateAndCreateRelativeConfig({
      cadence,
      offsetDays,
      offsetDayUnit,
      offsetNonBusinessDayAdjustment,
      period,
      weekdays: selectedDays.map((day) => dayOfTheWeekToNumberMap[day]),
      occurrence,
      startDateFormula,
      endDateFormula,
      frequency,
    });
    setConfig((current) => {
      const newArr = [...current];
      newArr[indexOfConfig] = {
        isValid: !!result.config,
        isRelative: true,
        startDateFormula,
        endDateFormula,
        touched: true,
        frequency,
        cadence,
        occurrence,
        period,
        offsetDays,
        offsetDayUnit,
        offsetNonBusinessDayAdjustment,
        selectedDays,
      };
      return newArr;
    });
  }, [
    cadence,
    endDateFormula,
    startDateFormula,
    frequency,
    indexOfConfig,
    occurrence,
    offsetDayUnit,
    offsetDays,
    offsetNonBusinessDayAdjustment,
    period,
    selectedDays,
    setConfig,
  ]);

  // --------------------- GENERATE HUMAN READABLE STRING ---------------------
  const getHumanReadableString = useCallback(() => {
    return generateHumanReadableString({
      cadence,
      offsetDayUnit,
      offsetDays,
      offsetNonBusinessDayAdjustment,
      period,
      startDate: startDateFormula,
      startDateNumber: undefined,
      endDate: endDateFormula,
      frequency,
      selectedDays,
      occurrence,
    });
  }, [
    cadence,
    endDateFormula,
    frequency,
    occurrence,
    offsetDayUnit,
    offsetDays,
    offsetNonBusinessDayAdjustment,
    period,
    selectedDays,
    startDateFormula,
  ]);

  return (
    <Stack spacing="20px">
      {numOfConfigs === 1 ? (
        <Box bg={greyBgColor} borderRadius="8px" padding="10px" fontSize="sm">
          {getHumanReadableString()}
        </Box>
      ) : (
        <HStack>
          <Box
            bg={greyBgColor}
            borderRadius="8px"
            padding="10px"
            fontSize="sm"
            w="95%"
          >
            {getHumanReadableString()}
          </Box>
          <Spacer />
          <IconButton
            aria-label="Delete config"
            icon={<Icon icon="trash-can" />}
            size="sm"
            variant="ghost"
            onClick={() => {
              setConfig((current) => {
                const newArr = [...current];
                newArr.splice(indexOfConfig, 1);
                return newArr;
              });
            }}
          />
        </HStack>
      )}

      <SimpleGrid
        columns={2}
        spacingX="40px"
        spacingY="20px"
        templateColumns="1fr 4fr"
      >
        <Text fontSize="md" mt={2}>
          Start Date
        </Text>
        <DateFormulaInput
          errors={errorStartDateFormula}
          formula={startDateFormula}
          setFormula={setStartDateFormula}
          isDisabled={!!staticStartDateFormula}
        />
        <Text fontSize="md" mt={2}>
          End Date
        </Text>
        <DateFormulaInput
          errors={errorEndDateFormula}
          formula={endDateFormula}
          setFormula={setEndDateFormula}
          isDisabled={!!staticEndDateFormula}
        />

        <Text fontSize="md" mt={2}>
          Period
        </Text>
        <Select
          value={period}
          options={PERIOD_OPTIONS}
          onChange={(value) => {
            setPeriod(value as dateType.RecurringDate.BaseDateConfigType);
            if (value === 'weekday') {
              setFrequency('week');
            }
            if (value !== 'weekday' && frequency === 'week') {
              setFrequency('month');
            }
            setSelectedDays([]);
          }}
        />

        <Text fontSize="md" mt={2}>
          Frequency
        </Text>
        <Select
          placeholderLabel="Select frequency"
          value={frequency}
          isInvalid={errorFrequency.length > 0}
          options={_.map(frequencyFilteredOptions, (option) => ({
            label: makeAdverb(option),
            value: option,
          }))}
          onChange={(value) => {
            setFrequency(
              value as dateType.RecurringDate.FrequencyPeriod | undefined,
            );
          }}
        />

        <Text fontSize="md" mt={2}>
          Repeat every
        </Text>
        <HStack spacing="20px" width="76%" borderRadius="8px" height="40px">
          <NumberInput
            value={cadence}
            min={1}
            onChange={(newValue) => setCadence(Number(newValue))}
            width="82px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>

          <Text fontSize="md">frequency</Text>
        </HStack>

        {(period === 'weekday' || period === 'weekdayByOccurrence') && (
          <Text fontSize="md" mt={2} />
        )}
        {(period === 'weekday' || period === 'weekdayByOccurrence') && (
          <DaysOfTheWeekSelectButtons
            onSelectDay={(day) => handleSelectDays(day, setSelectedDays)}
            selectedDays={selectedDays}
            error={errorSelectedDay}
          />
        )}

        {period === 'weekdayByOccurrence' && <Text fontSize="md" mt={2} />}
        {period === 'weekdayByOccurrence' && (
          <NumberInput
            value={ordinalSuffix(occurrence)}
            min={1}
            onChange={(newValue) => setOccurrence(Number(newValue))}
            width="82px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        )}

        <Text fontSize="md" mt={2}>
          Offset
        </Text>
        <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
          <NumberInput
            value={offsetDays}
            onChange={(newValue) => setOffsetDays(Number(newValue))}
            width="105px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Select
            value={offsetDayUnit}
            options={OFFSET_DAY_UNIT_OPTIONS}
            onChange={(value) => {
              if (value === 'businessDay') {
                setOffsetNonBusinessDayAdjustment(undefined);
              }
              setoffsetDayUnit(value as dateType.DayUnit);
            }}
          />
        </HStack>

        {/* Non business day adjustment checkbox */}
        {frequency !== undefined && offsetDayUnit === 'calendarDay' && (
          <Text fontSize="md" mt={2} />
        )}
        {frequency !== undefined && offsetDayUnit === 'calendarDay' && (
          <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
            <Checkbox
              isChecked={!!offsetNonBusinessDayAdjustment}
              onChange={(e) =>
                setOffsetNonBusinessDayAdjustment(
                  e.target.checked ? 'after' : undefined,
                )
              }
              fontSize="x-small"
              color={colors.gray[50]}
            >
              Business days only
            </Checkbox>

            {offsetNonBusinessDayAdjustment !== undefined && (
              <>
                <Text>-</Text>

                <Text>Find</Text>
                <Select
                  value={offsetNonBusinessDayAdjustment}
                  options={[
                    {
                      label: 'Prior',
                      value: 'before' as dateType.NonBusinessDayAdjustment,
                    },
                    {
                      label: 'Next',
                      value: 'after' as dateType.NonBusinessDayAdjustment,
                    },
                  ]}
                  onChange={(value) => {
                    setOffsetNonBusinessDayAdjustment(value);
                  }}
                  buttonProps={{ fontSize: 'small' }}
                />

                <Text>Business Day</Text>
              </>
            )}
          </HStack>
        )}
      </SimpleGrid>

      {(!config.isValid || sumOfInputErrors.length > 0) && (
        <HStack
          spacing="8px"
          bg="red.100"
          borderRadius="8px"
          padding="15px"
          fontSize="sm"
        >
          <WarningIcon color="red.500" />
          <Text color="red.500">{sumOfInputErrors}</Text>
        </HStack>
      )}

      <Divider orientation="horizontal" borderColor="gray.300" />

      <Spacer />
    </Stack>
  );
};

type GlobalConfigProps = {
  mode: 'global';
  savedConfig: CreditAgreement.Config.GlobalRecurringDate.Config;
  setSavedConfig: React.Dispatch<
    React.SetStateAction<CreditAgreement.Config.GlobalRecurringDate.Config>
  >;
  staticStartDateFormula?: string;
  staticEndDateFormula?: string;
  preSelectedTriggerDate?: Util.FullDateStr;
};

type SetupConfigProps = {
  mode: 'setup';
  savedConfig: dateType.RecurringDate.RelativeConfig;
  setSavedConfig: React.Dispatch<
    React.SetStateAction<dateType.RecurringDate.RelativeConfig>
  >;
  staticStartDateFormula?: string;
  staticEndDateFormula?: string;
  preSelectedTriggerDate?: Util.FullDateStr;
};

export const getRecurringDateConfigs = (
  savedConfig: dateType.RecurringDate.RelativeConfig,
) => {
  return savedConfig.relativeRecurringDateConfig.length > 0
    ? convertRelativeRecurringDateConfigsToConfigFormStates(
        savedConfig.relativeRecurringDateConfig,
      )
    : [];
};

export const getExcludedDateConfigs = (
  savedConfig: dateType.RecurringDate.RelativeConfig,
) => {
  return savedConfig.exclusions.length > 0
    ? convertRelativeRecurringDateConfigsToConfigFormStates(
        savedConfig.exclusions,
      )
    : [];
};

export type RelativeRecurringDateFormProps =
  | SetupConfigProps
  | GlobalConfigProps;

export const RelativeRecurringDateForm = ({
  mode,
  savedConfig,
  setSavedConfig,
  staticStartDateFormula,
  staticEndDateFormula,
  preSelectedTriggerDate,
}: RelativeRecurringDateFormProps) => {
  if (mode === 'global') {
    finleyAssert(
      savedConfig.setup.type === 'relative',
      'a Relative recurring date setup type is required to use the Relative Recurring Date Form',
    );
  }
  const getInitialRecurringDateConfigs = useCallback(() => {
    if (mode === 'setup') {
      return getRecurringDateConfigs(savedConfig);
    }
    if (mode === 'global' && savedConfig.setup.type === 'relative') {
      return getRecurringDateConfigs(savedConfig.setup.config);
    }

    return [{ isValid: false, isRelative: false, touched: false }];
  }, [mode, savedConfig]);

  const getInitialExcludedDateConfigs = useCallback(() => {
    if (mode === 'setup') {
      return getExcludedDateConfigs(savedConfig);
    }
    if (mode === 'global' && savedConfig.setup.type === 'relative') {
      return getExcludedDateConfigs(savedConfig.setup.config);
    }

    return [{ isValid: false, isRelative: false, touched: false }];
  }, [mode, savedConfig]);

  // configs
  const [recurringDateConfigs, setRecurringDateConfigs] = React.useState<
    ConfigFormState[]
  >(getInitialRecurringDateConfigs());

  const [excludedDateConfigs, setExcludedDateConfigs] = React.useState<
    ConfigFormState[]
  >(getInitialExcludedDateConfigs());

  // update state that is passed in
  useEffect(() => {
    const allRecurring = convertConfigFormStatesToRelativeRecurringDateConfigs(
      recurringDateConfigs.filter((c) => c.isValid && c.isRelative),
    );
    const allExcluded = convertConfigFormStatesToRelativeRecurringDateConfigs(
      excludedDateConfigs.filter((c) => c.isValid && c.isRelative),
    );
    const isDisabled =
      allRecurring.length < 1 || !recurringDateConfigs.every((c) => c.isValid);
    if (!isDisabled) {
      if (mode === 'setup') {
        setSavedConfig({
          relativeRecurringDateConfig: allRecurring,
          exclusions: allExcluded,
        });
      } else if (mode === 'global' && savedConfig.setup.type === 'relative') {
        setSavedConfig({
          ...savedConfig,
          setup: {
            ...savedConfig.setup,
            config: {
              relativeRecurringDateConfig: allRecurring,
              exclusions: allExcluded,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excludedDateConfigs,
    recurringDateConfigs,
    setSavedConfig,
    mode,
    // !TODO @eldarshamukhamedov Figure out why savedConfig is not listed in the deps
    // savedConfig
  ]);

  return (
    <Tabs>
      <TabList>
        <Tab>Recurring Dates</Tab>
        <Tab>Optional Exclusions</Tab>
      </TabList>

      <TabPanels>
        <TabPanel padding="24px 0">
          <Accordion defaultIndex={[0]} allowMultiple width="100%">
            <Stack spacing="25px">
              <AccordionItem
                borderWidth="1px"
                borderColor={colors.gray[200]}
                borderRadius="8px"
                padding="20px"
              >
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    <Heading as="h4" size="md">
                      Relative Recurring Dates
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel mt="16px" pb={4}>
                  <Flex direction="column">
                    {_.map(recurringDateConfigs, (config, i) => {
                      return (
                        <RecurringDatesOptionsGrid
                          key={JSON.stringify(config)}
                          config={config}
                          setConfig={setRecurringDateConfigs}
                          indexOfConfig={i}
                          numOfConfigs={recurringDateConfigs.length}
                          staticStartDateFormula={staticStartDateFormula}
                          staticEndDateFormula={staticEndDateFormula}
                        />
                      );
                    })}

                    <HStack>
                      <AddConfigButton
                        title="Add Recurring Dates"
                        configs={recurringDateConfigs}
                        setConfig={setRecurringDateConfigs}
                        isRelative
                      />
                      <Spacer />

                      {isAdminDashboard() &&
                        (() => {
                          if (mode === 'setup') {
                            return (
                              savedConfig.relativeRecurringDateConfig.length >
                                0 && (
                                <Button
                                  size="sm"
                                  aria-label="download relative date config"
                                  leftIcon={
                                    <Icon icon="arrow-down-to-bracket" />
                                  }
                                  width="fit-content"
                                  variant="ghost"
                                  onClick={() => {
                                    downloadRelativeConfig(savedConfig);
                                  }}
                                >
                                  Download Config JSON
                                </Button>
                              )
                            );
                          }

                          if (
                            mode === 'global' &&
                            savedConfig.setup.type === 'relative'
                          ) {
                            return (
                              savedConfig.setup.config
                                .relativeRecurringDateConfig.length > 0 && (
                                <Button
                                  size="sm"
                                  aria-label="download relative date config"
                                  leftIcon={
                                    <Icon icon="arrow-down-to-bracket" />
                                  }
                                  width="fit-content"
                                  variant="ghost"
                                  onClick={() => {
                                    if (savedConfig.setup.type === 'relative') {
                                      downloadRelativeConfig(
                                        savedConfig.setup.config,
                                      );
                                    }
                                  }}
                                >
                                  Download Config JSON
                                </Button>
                              )
                            );
                          }

                          return null;
                        })()}
                    </HStack>
                  </Flex>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem
                borderWidth="1px"
                borderColor={colors.gray[200]}
                borderRadius="8px"
                padding="20px"
              >
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    <Heading as="h4" size="md">
                      Test Trigger Date & Review Dates
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel mt="16px" pb={4}>
                  <TestTriggerDate
                    relativeConfigs={recurringDateConfigs}
                    excludedConfigs={excludedDateConfigs}
                    preSelectedTriggerDate={preSelectedTriggerDate}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Stack>
          </Accordion>
        </TabPanel>

        <TabPanel padding="24px 0">
          <Stack
            borderWidth="1px"
            borderColor={colors.gray[200]}
            borderRadius="8px"
            padding="20px"
            spacing="25px"
            width="100%"
          >
            <Heading as="h4" size="md">
              Optional Exclusions
            </Heading>

            <Text fontSize="md">The dates listed here will be excluded.</Text>
            <Flex direction="column">
              {_.map(excludedDateConfigs, (config, i) => {
                return (
                  <RecurringDatesOptionsGrid
                    key={JSON.stringify(config)}
                    config={config}
                    setConfig={setExcludedDateConfigs}
                    indexOfConfig={i}
                    numOfConfigs={excludedDateConfigs.length}
                  />
                );
              })}
              <AddConfigButton
                title="Add Exclusion Dates"
                configs={excludedDateConfigs}
                setConfig={setExcludedDateConfigs}
                isRelative
              />
            </Flex>
          </Stack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
